import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, RotateCcw, ChevronRight, Loader2, Filter, FileText, RefreshCcw } from 'lucide-react';
import { certListProjects, certGetReportTypes } from '../../services/reportingHubApi';


const PAGE_SIZE = 10;

// Tokenize model names into suggested-filter chips (frequency ranked).
function buildChips(items, max = 16) {
  const freq = new Map();
  for (const it of items) {
    const seen = new Set();
    String(it.model).split(/[_()/\s]+/).forEach((tok) => {
      const t = tok.trim();
      if (!t || t.length < 2 || seen.has(t.toUpperCase())) return;
      seen.add(t.toUpperCase());
      freq.set(t, (freq.get(t) || 0) + 1);
    });
  }
  return Array.from(freq.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, max)
    .map(([token, count]) => ({ token, count }));
}

const CertificationSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const type = params.get('type') || '';
  const [items, setItems] = useState([]);
  const [keyword, setKeyword] = useState(
    location.state?.keyword || ''
  );

  const [selected, setSelected] = useState(
    location.state?.selected || []
  );
  const [loading, setLoading] = useState(true);
  const [typeLabel, setTypeLabel] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);


  useEffect(() => {
    certGetReportTypes().then((d) => {
      const rt = (d.reportTypes || []).find((r) => r.key === type);
      setTypeLabel(rt ? rt.label : type);
    }).catch(() => { });
  }, [type]);

  useEffect(() => {
    setLoading(true);
    certListProjects('')
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const chips = useMemo(() => buildChips(items), [items]);

  const filtered = useMemo(() => {
    const terms = [...keyword.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean), ...selected];
    if (terms.length === 0) return items;
    return items.filter((i) => {
      const m = String(i.model).toLowerCase();
      return terms.every((t) => m.includes(t.toLowerCase()));
    });
  }, [items, keyword, selected]);

  const toggle = (token) => setSelected((p) => (p.includes(token) ? p.filter((t) => t !== token) : [...p, token]));
  const reset = () => { setKeyword(''); setSelected([]); };

  // Reset pagination whenever the filter criteria or dataset changes.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [keyword, selected, items]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;
  const showMore = () => setVisibleCount((c) => c + PAGE_SIZE);
  // const openProject = (model) => navigate(`/reporting-hub/certification/view?type=${type}&model=${encodeURIComponent(model)}`);
  const openProject = (model) =>
    navigate(
      `/reporting-hub/certification/view?type=${type}&model=${encodeURIComponent(model)}`,
      {
        state: {
          keyword,
          selected,
        },
      }
    );

  return (
    <div className="min-h-screen bg-black text-white p-2 sm:p-2 md:p-2 lg:px-5 lg:py-4 mb-1">
      <div className="w-full mx-auto">
        <div className="flex items-center gap-1 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-400 sm:mb-5 sm:-ml-4">
          <button
            onClick={() => navigate('/reporting-hub/certification')}
            className="p-1 sm:p-2 rounded-full hover:bg-gray-800 transition-colors flex-shrink-0"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 sm:w-5 sm:h-5 md:w-7 md:h-7 font-bold" />
          </button>

          <h1
            className="
      text-base
      sm:text-lg
      md:text-2xl
      lg:text-2xl
      xl:text-2xl
      font-bold
      leading-tight
      break-words
    "
          >
            Project Reporting Hub_Certification
          </h1>
        </div>

        <p
          className="
    text-xs
    sm:text-sm
    md:text-base
    text-gray-400
    ml-2 sm:ml-3
    mt-3 sm:mt-4
    mb-4 sm:mb-6
    break-words
  "
        >
          Report type:
          <span className="text-gray-200 ml-1">
            {typeLabel}
          </span>
        </p>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
          <label className="font-semibold p-2 text-xs sm:text-sm md:text-base lg:text-lg">
            Keyword search
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:mb-5">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-black" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Search by project ID, project name, region, platform, vendor, model name"
              className="
        w-full
        bg-white
        text-black
        rounded-lg
        pl-10 sm:pl-11
        pr-4
        py-2.5 sm:py-3
        text-sm sm:text-base
        outline-none
        focus:ring-2
        focus:ring-blue-500
      "
            />
          </div>

          {/* Mobile Reset Button */}
          <div className="flex justify-end sm:hidden">
            <button
              onClick={reset}
              className="
        flex items-center gap-1
        px-3 py-2
        rounded-lg
        bg-gray-700
        hover:bg-gray-600
        transition-colors
        text-sm
      "
            >
              Reset
              <RefreshCcw className="w-4 h-4" />
            </button>
          </div>

          {/* Desktop Reset Button */}
          <button
            onClick={reset}
            className="
      hidden sm:flex
      items-center gap-2
      px-4 py-3
      rounded-lg
      bg-gray-700
      hover:bg-gray-600
      transition-colors
      whitespace-nowrap
    "
          >

            Reset
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
          <label className="font-semibold p-2 text-xs sm:text-sm md:text-base lg:text-lg">
            Suggested filters
          </label>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2 sm:gap-2 mb-3 pb-3 sm:pb-4 md:pb-5 border-b-2 border-gray-400">
          {chips.length === 0 && (
            <span className="text-gray-500 text-sm col-span-full">
              No suggestions
            </span>
          )}

          {chips.map((chip) => {
            const active = selected.includes(chip.token);

            return (
              <button
                key={chip.token}
                onClick={() => toggle(chip.token)}
                title={`${chip.count} projects`}
                className={`w-full px-1.5 sm:px-3 py-1 sm:py-2 rounded-full text-[10px] sm:text-xs md:text-sm font-medium transition-colors truncate ${active
                    ? "bg-gray-500 text-white"
                    : "bg-white text-black hover:bg-gray-200"
                  }`}
              >
                {chip.token}
              </button>
            );
          })}
        </div>

        <hr className="border-gray-800 mb-4" />

        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />

            <h2 className="block font-semibold p-2 text-xs sm:text-sm md:text-base lg:text-lg">
              Results{" "}
              <span className="text-gray-400 text-sm">
                ({filtered.length})
              </span>
            </h2>
          </div>

          <span className="block font-semibold p-2 text-xs sm:text-sm md:text-base lg:text-base text-gray-200">
            Tips: Add keywords or select more filters to narrow down the list
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <Loader2 className="sm:w-15 sm:h-15 w-6 h-6 animate-spin text-blue-800" />
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.length === 0 && (
              <div className="text-center text-gray-500 py-10">
                No projects found.
              </div>
            )}

            {visible.map((p) => (
              <button
                key={p.model}
                onClick={() => openProject(p.model)}
                className="
          w-full
          flex flex-col sm:flex-row
          sm:items-center sm:justify-between
          gap-2
          px-3 sm:px-4 md:px-5
          py-3 sm:py-4
          bg-black
          hover:bg-gray-800
          border-b-2 border-dotted border-gray-400
          text-left
          transition-colors
          text-white
        "
              >
                <span
                  className="
            font-mono
            text-xs sm:text-sm md:text-base
            break-words
            w-full
            text-white
          "
                >
                  {p.model}
                </span>

                <span
                  className="
            flex items-center gap-2 sm:gap-3
            text-gray-400
            text-[10px] sm:text-xs md:text-sm
            self-end sm:self-center
            flex-shrink-0
          "
                >
                  <span>{p.total} items</span>

                  <ChevronRight
                    className="
              w-5 h-5 sm:w-7 sm:h-7
              text-gray-400
              flex-shrink-0
            "
                  />
                </span>
              </button>
            ))}

            {hasMore && (
              <div className="flex justify-center mt-5">
                <button
                  onClick={showMore}
                  className="px-6 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
                >
                  Show more
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default CertificationSearch;
